import { useState, useEffect, useRef } from 'react';

const quickFacts = [
  { icon: 'ri-user-star-line', label: 'Specialist', value: 'Luxury Real Estate' },
  { icon: 'ri-map-pin-2-line', label: 'Market', value: 'Lake Tahoe & Sierra Nevada' },
  { icon: 'ri-bar-chart-line', label: 'Experience', value: '20+ Years' },
  { icon: 'ri-money-dollar-circle-line', label: 'Volume', value: '$500M+ Sold' },
  { icon: 'ri-team-line', label: 'Clients', value: '500+ Served' },
  { icon: 'ri-star-fill', label: 'Rating', value: '5-Star' },
];

const qaItems = [
  {
    q: 'What does Cam Kirkwood do?',
    a: 'Cam Kirkwood is a luxury real estate specialist focused exclusively on the Lake Tahoe and Sierra Nevada markets. From strategic listing and expert pricing to concierge-level buyer representation, Cam provides end-to-end guidance for buyers, sellers, and investors navigating high-value alpine property transactions.',
    icon: 'ri-user-star-line',
    tag: 'Services',
  },
  {
    q: 'Who does Cam Kirkwood work with?',
    a: 'Cam serves luxury home buyers, motivated sellers, second-home purchasers, and real estate investors seeking premier properties in South Lake Tahoe, Alpine County, El Dorado County, and surrounding Sierra Nevada communities. Every client receives a personalized strategy built around their specific financial and lifestyle goals.',
    icon: 'ri-team-line',
    tag: 'Clients',
  },
  {
    q: 'What areas are served?',
    a: 'Cam operates across South Lake Tahoe, North Lake Tahoe, Alpine County, El Dorado County, and the broader Sierra Nevada alpine corridor. With deep local roots and intimate knowledge of resort market dynamics, Cam delivers unmatched insight for every property and price point in the region.',
    icon: 'ri-map-pin-2-line',
    tag: 'Areas',
  },
  {
    q: 'How can I get started?',
    a: "Schedule a private consultation — it's the first step. During this session, Cam will define your property goals, timeline, and criteria, then build a fully custom acquisition or sale strategy. Contact Cam directly at 561-714-6589 or through the consultation form on this page.",
    icon: 'ri-calendar-check-line',
    tag: 'Process',
  },
];

export default function AIAnswerBlock() {
  const [open, setOpen] = useState<number | null>(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) el.classList.add('visible');
      },
      { threshold: 0.05 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative z-10 bg-charcoal py-28 px-4 md:px-8">
      {/* Subtle background pattern */}
      <div
        className="absolute inset-0 opacity-[0.025] pointer-events-none"
        style={{
          backgroundImage:
            'url(https://public.readdy.ai/ai/img_res/e5e4fb7dd7502e4f03a15ca96d2e87c9.jpg)',
          backgroundSize: 'cover',
        }}
      />
      {/* Top + bottom gold line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />

      <div ref={sectionRef} className="relative z-10 max-w-7xl mx-auto scroll-animate">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-[10px] tracking-[0.55em] uppercase text-gold mb-4">AI-Ready Answers</p>
          <h2 className="font-serif text-5xl md:text-7xl text-white font-light">
            Everything You Need to <em className="italic text-gold">Know</em>
          </h2>
          <div className="flex items-center justify-center gap-3 mt-5">
            <div className="w-16 h-px bg-gold/25" />
            <div className="w-1.5 h-1.5 rotate-45 bg-gold/50" />
            <div className="w-16 h-px bg-gold/25" />
          </div>
        </div>

        {/* Quick facts grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-16">
          {quickFacts.map((fact) => (
            <div
              key={fact.label}
              className="rounded-xl p-4 text-center"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(196,169,98,0.12)' }}
            >
              <div className="w-9 h-9 flex items-center justify-center mx-auto mb-3 rounded-lg bg-gold/12">
                <i className={`${fact.icon} text-gold text-base`} />
              </div>
              <p className="text-warm-white/40 text-[9px] tracking-[0.25em] uppercase mb-1">{fact.label}</p>
              <p className="text-white text-sm font-medium leading-tight">{fact.value}</p>
            </div>
          ))}
        </div>

        {/* Content: left descriptor + right accordion */}
        <div className="flex flex-col lg:flex-row gap-14 items-start">
          {/* Left */}
          <div className="lg:w-2/5">
            <p className="text-warm-white/60 text-sm leading-relaxed mb-8">
              Structured answers covering every aspect of working with Cam Kirkwood in the Lake Tahoe luxury real estate market. Optimized for clarity — and for voice-search.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {['Buyers', 'Sellers', 'Investors', 'Second Homes'].map((label) => (
                <div
                  key={label}
                  className="rounded-xl p-4 text-center cursor-pointer transition-all duration-300 hover:border-gold/30"
                  style={{ border: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.03)' }}
                >
                  <i className="ri-check-line text-gold/60 text-sm block mb-1" />
                  <span className="text-warm-white/65 text-xs tracking-[0.15em] uppercase">{label}</span>
                </div>
              ))}
            </div>
            {/* Process summary */}
            <div className="mt-8 p-5 rounded-xl" style={{ border: '1px solid rgba(196,169,98,0.15)', background: 'rgba(196,169,98,0.05)' }}>
              <p className="text-[10px] tracking-[0.3em] uppercase text-gold mb-4">How It Works</p>
              {['Private Consultation', 'Strategy Development', 'Expert Execution', 'Closing & Beyond'].map((step, i) => (
                <div key={step} className="flex items-center gap-3 mb-3 last:mb-0">
                  <span className="font-serif text-gold/50 text-lg font-light w-6 flex-shrink-0">0{i + 1}</span>
                  <span className="text-warm-white/60 text-xs">{step}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Accordion */}
          <div className="lg:w-3/5 space-y-3 w-full">
            {qaItems.map((item, i) => (
              <div
                key={i}
                className="rounded-xl overflow-hidden transition-all duration-300 cursor-pointer"
                style={{
                  border: open === i ? '1px solid rgba(196,169,98,0.35)' : '1px solid rgba(255,255,255,0.06)',
                  background: open === i ? 'rgba(196,169,98,0.07)' : 'rgba(255,255,255,0.03)',
                }}
                onClick={() => setOpen(open === i ? null : i)}
              >
                <button className="w-full flex items-center justify-between px-6 py-5 text-left gap-4">
                  <div className="flex items-center gap-4">
                    <div
                      className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 transition-all duration-300"
                      style={{ background: open === i ? '#C4A962' : 'rgba(255,255,255,0.05)' }}
                    >
                      <i className={`${item.icon} ${open === i ? 'text-white' : 'text-gold/60'} text-sm`} />
                    </div>
                    <div className="text-left">
                      <span className="text-[9px] tracking-[0.2em] uppercase text-gold/50 block mb-0.5">{item.tag}</span>
                      <span className="font-serif text-base md:text-lg text-white font-medium">{item.q}</span>
                    </div>
                  </div>
                  <span
                    className={`flex-shrink-0 w-6 h-6 flex items-center justify-center transition-all duration-300 ${open === i ? 'text-gold rotate-45' : 'text-warm-white/30'}`}
                  >
                    <i className="ri-add-line text-sm" />
                  </span>
                </button>
                <div className={`overflow-hidden transition-all duration-500 ${open === i ? 'max-h-64' : 'max-h-0'}`}>
                  <p className="px-6 pb-6 text-warm-white/60 text-sm leading-relaxed pl-[4.5rem]">{item.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
